// src/cockpit4/components/BrandMonitorsHub.tsx
// ============================================
// GEO+DSO 品牌监测中心（升级：3 个二级页签）
//   ① 数据大盘（DataBigscreenPanel）—— 截图1
//   ② 提及率和排名（MentionRankingPanel）—— 截图2 + 截图3
//   ③ 品牌舆情追踪（SentimentTrackerPanel）—— 截图4 + 截图5
// ============================================

import { useEffect, useMemo, useState } from "react";
import {
  Radar, RefreshCw, LayoutDashboard, BarChart3,
  MessageCircle, ChevronDown, Plus,
} from "lucide-react";
import { motion } from "framer-motion";
import DataBigscreenPanel from "./DataBigscreenPanel";
import MentionRankingPanel from "./MentionRankingPanel";
import SentimentTrackerPanel from "./SentimentTrackerPanel";
import {
  aggregateMonitorData,
  fetchAIVisibilityHistory,
  ALL_MONITOR_PLATFORMS,
  MONITOR_QUESTIONS,
  type BrandMonitorData,
  type AIVisibilityHistory,
} from "../../runtime/GrowthFactory/brandMonitorData";
import type { GEODiagnosticRuntime } from "../../runtime/GrowthFactory/GEODiagnosticRuntime";
import type {
  DiagPlatformKey,
  RealTimeSearchResult,
} from "../../runtime/GrowthFactory/types";

type TabKey = "bigscreen" | "mention" | "sentiment";

interface Props {
  runtime: GEODiagnosticRuntime;
  /** 预留：默认监测品牌（从诊断面板获取或填入） */
  brand?: string;
  /** 点击「+ 增加品牌监测」时触发（由父级打开向导 Modal） */
  onAddBrand: () => void;
  /** 预留：实时诊断结果（若已运行，可直接注入） */
  existingResults?: Array<{
    qid: string;
    q: string;
    type: "产品选型" | "行业定义" | "落地案例" | "原理关系";
    heat: number;
    result: RealTimeSearchResult;
  }>;
}

const TABS: { key: TabKey; label: string; icon: any; color: string }[] = [
  { key: "bigscreen", label: "数据大盘", icon: LayoutDashboard, color: "#8B5CF6" },
  { key: "mention", label: "提及率和排名", icon: BarChart3, color: "#22D3EE" },
  { key: "sentiment", label: "品牌舆情追踪", icon: MessageCircle, color: "#EC4899" },
];

const DEFAULT_DATE_RANGE = () => {
  const today = new Date();
  const start = new Date(today.getTime() - 30 * 24 * 3600 * 1000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { s: fmt(start), e: fmt(today) };
};

export default function BrandMonitorsHub({ runtime, brand = "小舍科技", onAddBrand, existingResults }: Props) {
  const [tab, setTab] = useState<TabKey>("bigscreen");
  const def = DEFAULT_DATE_RANGE();
  const [dateStart, setDateStart] = useState(def.s);
  const [dateEnd, setDateEnd] = useState(def.e);
  const [platforms, setPlatforms] = useState<string[]>(ALL_MONITOR_PLATFORMS.map((p) => p.key));
  const allQids = MONITOR_QUESTIONS.map((q) => q.id);
  const [questionsFilter, setQuestionsFilter] = useState<string[]>(allQids);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState("");
  const [monitorBrand, setMonitorBrand] = useState(brand);
  const [brandOpen, setBrandOpen] = useState(false);
  const [lastRun, setLastRun] = useState("");
  const [history, setHistory] = useState<AIVisibilityHistory | undefined>(undefined);
  // 品牌下拉：后端持久化品牌监测计划（真实配置）+ 公司默认品牌
  const [brandOptions, setBrandOptions] = useState<string[]>(["小舍科技", "XEAOS", "小舍AI"]);
  const [searchResults, setSearchResults] = useState<
    Array<{
      qid: string; q: string; type: string; heat: number; result: RealTimeSearchResult;
    }>
  >(existingResults || []);

  // 拉取服务端真实 AI 可见性历史 + 品牌监测计划（无模拟数据）
  useEffect(() => {
    fetchAIVisibilityHistory(30).then(setHistory);
    fetch("/ai-visibility/brands").then((r) => r.json()).then((d) => {
      if (d?.success && Array.isArray(d.brands)) {
        const names = d.brands.map((b: any) => b.brandName).filter(Boolean);
        if (names.length > 0) setBrandOptions(Array.from(new Set([...names, ...brandOptions])));
      }
    }).catch(() => { /* 后端不可达时保留默认品牌 */ });
  }, []);

  useEffect(() => {
    if (existingResults && existingResults.length > 0) {
      setSearchResults(existingResults);
    }
  }, [existingResults]);

  const questions = useMemo(
    () => MONITOR_QUESTIONS.map((q) => ({
      id: q.id,
      label: `${q.q.slice(0, 18)}${q.q.length > 18 ? "…" : ""}`,
    })),
    []
  );

  const data: BrandMonitorData = useMemo(
    () => aggregateMonitorData(monitorBrand, searchResults, dateStart, dateEnd),
    [monitorBrand, searchResults, dateStart, dateEnd]
  );

  const runNow = async () => {
    setRunning(true);
    setProgress("");
    setSearchResults([]);
    const questions = MONITOR_QUESTIONS;
    // 取已启用的原生平台键（去重）
    const nativeKeys = ALL_MONITOR_PLATFORMS
      .filter((p) => p.enabled && platforms.includes(p.key) && p.nativeKey)
      .map((p) => p.nativeKey as DiagPlatformKey);
    const targetNative = Array.from(new Set(nativeKeys));
    const totalQ = questions.length;
    const out: any[] = [];
    try {
      for (let i = 0; i < totalQ; i++) {
        const q = questions[i];
        const pcb = (msg: string) => setProgress(`[${i + 1}/${totalQ}] ${msg}`);
        setProgress(`[${i + 1}/${totalQ}] 询问：${q.q.slice(0, 18)}…`);
        const res = await runtime.diagnoseRealTime(q.q, monitorBrand, targetNative, pcb);
        out.push({ qid: q.id, q: q.q, type: q.type, heat: q.heat, result: res });
      }
      setProgress("监测完成");
      setSearchResults(out);
      setLastRun(new Date().toLocaleString("zh-CN"));
    } catch (e: any) {
      setProgress(`监测失败：${e?.message || String(e)}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-160px)]">
      {/* 顶部栏 */}
      <div className="px-4 pt-3 pb-2 flex flex-wrap items-center justify-between gap-2 border-b border-white/[0.05]">
        <div className="flex items-center gap-2">
          <Radar size={16} className="text-cyan-400" />
          <span className="text-[13px] font-semibold text-white tracking-tight">
            GEO+DSO 品牌监测中心
          </span>
          <span className="text-[10px] font-mono text-gray-500 ml-2">
            真实 AI 结果 · 提及率 · 情感 · 引用文章
          </span>
          {lastRun && (
            <span className="text-[10px] font-mono text-gray-600 ml-2">最近：{lastRun}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {progress && (
            <motion.span
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="text-[10px] font-mono text-cyan-300/80 px-2 py-1 rounded bg-cyan-500/5"
            >
              {progress}
            </motion.span>
          )}
          <div className="relative">
            <button
              onClick={() => setBrandOpen((o) => !o)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px]
                       bg-black/30 border border-white/[0.08] text-white"
            >
              <span className="w-2 h-2 rounded-full bg-violet-500" /> {monitorBrand}
              <ChevronDown size={12} />
            </button>
            {brandOpen && (
              <div className="absolute top-full mt-1 right-0 z-30 w-72 p-2 rounded-md bg-[#0e1220] border border-white/[0.1] shadow-2xl">
                <div className="text-[10px] text-gray-500 mb-1">选择监测品牌</div>
                {["小舍科技", "XEAOS", "小舍AI", "自定义…"].map((b) => (
                  <button
                    key={b}
                    onClick={() => {
                      if (b === "自定义…") {
                        const v = window.prompt("输入监测品牌名", monitorBrand);
                        if (v) setMonitorBrand(v);
                      } else setMonitorBrand(b);
                      setBrandOpen(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-[11px] ${
                      monitorBrand === b ? "text-white bg-violet-500/15" : "text-gray-300 hover:bg-white/5"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onAddBrand}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px]
                       bg-white/[0.03] border border-white/[0.08] text-gray-400
                       hover:text-white hover:border-violet-500/40"
          >
            <Plus size={12} /> 增加品牌监测
          </button>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={runNow}
            disabled={running}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium
                       bg-violet-500/15 text-violet-300 border border-violet-500/30
                       hover:bg-violet-500/25 disabled:opacity-50"
          >
            <RefreshCw size={12} className={running ? "animate-spin" : ""} />
            {running ? "监测中…" : "立即监测"}
          </motion.button>
        </div>
      </div>

      {/* 二级 Tab 条 */}
      <div className="flex items-center gap-1 px-4 pt-2 border-b border-white/[0.04]">
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-t-md text-[11px] font-semibold transition-all border-b-2 ${
                on
                  ? "text-white border-[currentColor] bg-white/[0.03]"
                  : "text-gray-400 border-transparent hover:text-gray-200"
              }`}
              style={on ? { color: t.color } : {}}
            >
              <Icon size={12} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* 内容区 */}
      <div className="flex-1 overflow-auto">
        {tab === "bigscreen" && (
          <DataBigscreenPanel
            data={data}
            brand={monitorBrand}
            onBrandChange={setMonitorBrand}
            onAddBrand={onAddBrand}
            platforms={platforms}
            onPlatformsChange={setPlatforms}
            questions={questions}
            questionsFilter={questionsFilter}
            onQuestionsChange={setQuestionsFilter}
            dateStart={dateStart}
            dateEnd={dateEnd}
            onDateChange={(s, e) => { setDateStart(s); setDateEnd(e); }}
          />
        )}
        {tab === "mention" && (
          <MentionRankingPanel
            data={data}
            brand={monitorBrand}
            onAddBrand={onAddBrand}
            platforms={platforms}
            onPlatformsChange={setPlatforms}
            questions={questions}
            questionsFilter={questionsFilter}
            onQuestionsChange={setQuestionsFilter}
            dateStart={dateStart}
            dateEnd={dateEnd}
            onDateChange={(s, e) => { setDateStart(s); setDateEnd(e); }}
          />
        )}
        {tab === "sentiment" && (
          <SentimentTrackerPanel
            data={data}
            brand={monitorBrand}
            onAddBrand={onAddBrand}
            platforms={platforms}
            onPlatformsChange={setPlatforms}
            questions={questions}
            questionsFilter={questionsFilter}
            onQuestionsChange={setQuestionsFilter}
            dateStart={dateStart}
            dateEnd={dateEnd}
            onDateChange={(s, e) => { setDateStart(s); setDateEnd(e); }}
          />
        )}
      </div>
    </div>
  );
}
